import type { SoundEmitterRef } from '../scene/SceneManifest';
import type { AudioEngine } from './AudioEngine';
import type { AudioSource } from './AudioSource';

/**
 * Single-emitter lifecycle handle. Owned by `Scene.soundEmitters` (one entry
 * per loaded emitter id). Created on:
 *
 *  - Manifest-globals load (out-of-bounds + non-tiled scenes): one per entry
 *    in `manifest.soundEmitters`.
 *  - Tile-streamed load: one per entry in the SOUND_EMITTERS chunk.
 *
 * Destroyed when its source goes away (scene detach, tile evict). The runtime
 * runs in one of two modes:
 *
 *  - **Bed mode** (`spawnIntervalSec` null/0): owns one looping `AudioSource`
 *    positioned at the fixed emitter location. If the clip isn't cached when
 *    construction runs, `bedSource` stays null and `tick()` retries each
 *    frame — matches the `AmbientZoneController` retry contract. A permanent
 *    miss (e.g. misconfigured clipId 404ing) is warned about once and then
 *    stays silent for the rest of the session.
 *
 *  - **Spawn mode** (`spawnIntervalSec > 0`): no persistent source. `tick()`
 *    fires `playOneShot` at the fixed emitter position once `performance.now()`
 *    catches up to `nextSpawnAtMs`, then re-arms with a jittered interval. The
 *    first fire is also jittered so simultaneous tile loads don't sync up.
 */
export class SoundEmitterRuntime {
  private bedSource: AudioSource | null = null;
  private nextSpawnAtMs: number | null = null;
  private destroyed = false;
  /** One-shot guard so a permanently-missing clip warns once instead of every tick. */
  private failedClipWarned = false;
  /** `performance.now()` of the first failure since the last success; null when never failed or just recovered. */
  private firstFailureMs: number | null = null;
  /**
   * Cached fixed position so per-fire / per-retry paths don't allocate a new
   * array. Shared by reference into `playOneShot` and `createSourceFromCached`;
   * those call paths must not mutate the array. (Voice/AudioSource copy the
   * scalars into panner AudioParams at assignment time, so this is safe today.)
   */
  private readonly position: [number, number, number];

  /**
   * Wall-clock milliseconds of continuous failure before we surface a warn.
   * Wall-clock (not retry-count) is intentional: bed mode retries every frame
   * (~60/s) but spawn mode retries only once per `spawnIntervalSec`. A
   * frame-count threshold would be unreachable in spawn mode at typical
   * 10–30 s intervals. 5 s is comfortably past a normal clip load and short
   * enough that a misconfigured emitter surfaces during a dev session.
   */
  private static readonly WARN_THRESHOLD_MS = 5_000;

  constructor(
    private readonly audio: AudioEngine,
    private readonly ref: SoundEmitterRef,
  ) {
    this.position = [
      ref.position[0] ?? 0,
      ref.position[1] ?? 0,
      ref.position[2] ?? 0,
    ];
    if (this.isSpawnMode) {
      this.nextSpawnAtMs = performance.now() + this.jitteredIntervalMs();
    } else {
      this.startBed();
    }
  }

  get id(): string {
    return this.ref.id;
  }

  /** Exposed so tile-stream evict can call `library.release(clipId)` symmetrically. */
  get clipId(): string {
    return this.ref.clipId;
  }

  get isSpawnMode(): boolean {
    return (this.ref.spawnIntervalSec ?? 0) > 0;
  }

  /** Per-frame tick. Cheap when bed source is healthy + spawn timer hasn't elapsed. */
  tick(): void {
    if (this.destroyed) return;
    if (!this.isSpawnMode) {
      // Retry bed start if the clip wasn't cached when we constructed.
      if (!this.bedSource) this.startBed();
      return;
    }
    if (this.nextSpawnAtMs !== null && performance.now() >= this.nextSpawnAtMs) {
      const voice = this.audio.playOneShot(this.ref.clipId, {
        bus: 'ambient',
        volume: this.ref.volume ?? 1,
        position: this.position,
      });
      if (voice) {
        this.firstFailureMs = null;
      } else {
        this.maybeWarnMissingClip();
      }
      this.nextSpawnAtMs = performance.now() + this.jitteredIntervalMs();
    }
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.bedSource?.destroy();
    this.bedSource = null;
    this.nextSpawnAtMs = null;
  }

  private startBed(): void {
    const source = this.audio.createSourceFromCached(this.ref.clipId, {
      bus: 'ambient',
      volume: this.ref.volume ?? 1,
      loop: true,
      position: this.position,
    });
    if (!source) {
      this.maybeWarnMissingClip();
      return;
    }
    source.play();
    this.bedSource = source;
    this.firstFailureMs = null;
  }

  /**
   * Library clears its entry on permanent fetch failure, so a misconfigured
   * clipId keeps `createSourceFromCached` / `playOneShot` returning null. Wait
   * long enough that a normal slow load can't trip the warn, then surface it
   * once. Wall-clock gated so both bed mode (per-frame retry) and spawn mode
   * (per-interval retry) warn after the same elapsed time.
   */
  private maybeWarnMissingClip(): void {
    if (this.failedClipWarned) return;
    const now = performance.now();
    if (this.firstFailureMs === null) {
      this.firstFailureMs = now;
      return;
    }
    if (now - this.firstFailureMs < SoundEmitterRuntime.WARN_THRESHOLD_MS) return;
    this.failedClipWarned = true;
    const mode = this.isSpawnMode ? 'spawn' : 'bed';
    console.warn(
      `[sound-emitter ${this.ref.id}] ${mode} clip '${this.ref.clipId}' never loaded; check the asset reference`,
    );
  }

  /** plus or minus 50% jitter around the mean interval, in milliseconds. */
  private jitteredIntervalMs(): number {
    const mean = this.ref.spawnIntervalSec ?? 0;
    if (mean <= 0) return Number.POSITIVE_INFINITY;
    const jitterScale = 0.5 + Math.random();
    return mean * jitterScale * 1000;
  }
}
